


import React, { useState, useEffect } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
} from "recharts";
import { Box, CircularProgress, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { BASE_URL } from "../../../components/BaseUrl";
import { decryptToken } from "../../../Utlis/Encryption";
import { showUnderDevelopment } from "../../../components/alerts";
import chart1 from "../../../assets/chart1.svg";
import chart2 from "../../../assets/chart2.svg";
import CustomText, { textStyles } from "../../../components/CustomText";

const ActiveInactiveChart = () => {
  const { t } = useTranslation();
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(false);

  const rawEncryptedToken = localStorage.getItem("token");
  const token = rawEncryptedToken ? decryptToken(rawEncryptedToken) : null;

  useEffect(() => {
    async function fetchData() {
      try {
        setLoading(true);
        const res = await fetch(`${BASE_URL}/account-executive/company-counts`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });
        if (!res.ok) throw new Error("Failed to fetch");
        const json = await res.json();

        if (!json.error && json.data?.breakdown) {
          const { active, trial, inactive } = json.data.breakdown;

          // Recharts expects an array of objects
          setChartData([
            {
              name: t("reportschart.label"), // e.g. "Companies"
              ActiveUser: active.count,
              Inactive: inactive.count,
            },
          ]);
        }
      } catch (err) {
        console.error("❌ Error fetching company counts:", err);
        setChartData([]);
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, []);

  // Custom Tooltip to show translated labels on hover
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <Box
          sx={{
            backgroundColor: '#fff',
            border: '1px solid #ccc',
            borderRadius: '4px',
            padding: '8px 12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          }}
        >
          <Typography sx={{ fontSize: '12px', fontWeight: 600, mb: 0.5 }}>
            {label}
          </Typography>
          {payload.map((entry, index) => (
            <Typography
              key={index}
              sx={{
                fontSize: '12px',
                color: entry.color,
                fontWeight: 500,
              }}
            >
              {entry.dataKey === 'ActiveUser'
                ? t('reportschart.tooltip.activeUser')
                : entry.dataKey === 'Inactive'
                ? t('reportschart.tooltip.inactive')
                : entry.dataKey}: {entry.value}
            </Typography>
          ))}
        </Box>
      );
    }
    return null;
  };

  return (
    <Box
      sx={{
        px: 2,
        py: 2,
        pb: 5,
        backgroundColor: "white",
        border: "1px solid #091E4224",
        borderRadius: 2,
        boxShadow: "0px 0px 10px rgba(0,0,0,0.05)",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 5,
        }}
      >
        <CustomText
          sx={{
            ...textStyles.h1,
            fontSize: 15,
            fontWeight: 500,
            color: "#212121",
          }}
        >
          {t("reportschart.title")}
        </CustomText>

     
      </Box>

      {/* Chart */}
      <Box sx={{ height: 350 }}>
        {loading ? (
          <Box
            sx={{
              height: "100%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <CircularProgress size={30} />
          </Box>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={chartData}
              barCategoryGap="30%"
              barGap={0}
              margin={{ top: 10, right: 10, left: 0, bottom: 10 }}
            >
              <CartesianGrid stroke="#f5f5f5" vertical={false} />
              <XAxis dataKey="name" tick={{ fontSize: 13 }} />
              <YAxis tick={{ fontSize: 13 }} allowDecimals={false} />
              <Tooltip content={<CustomTooltip />} />
              <Legend
                wrapperStyle={{ fontSize: 13 }}
                formatter={(value) => {
                  if (value === "ActiveUser")
                    return t("reportschart.active_user");
                  if (value === "Inactive") return t("reportschart.inactive");
                  return value;
                }}
              />
              <Bar
                dataKey="ActiveUser"
                fill="#4BCE97"
                barSize={20}
                radius={[6, 6, 0, 0]}
              />
         
              <Bar
                dataKey="Inactive"
                fill="#F87168"
                barSize={20}
                radius={[6, 6, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        )}
      </Box>
    </Box>
  );
};

export default ActiveInactiveChart;
